import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { Game } from '@/types'
import { GameCover } from '@/components/ui/GameCover'
import { CoopAttribution, CoopKindBadge, CoopModesBadges, CoopPlayersBadge } from '@/components/ui/CoopBadges'
import { HltbTimes } from '@/components/ui/HltbBadge'
import { RatingPill } from '@/components/ui/RatingPill'
import { PriceTag } from '@/components/ui/PriceTag'
import { Icon } from '@/components/ui/Icon'

interface GameDetailModalProps {
  game: Game
  groupSize: number
  onClose: () => void
}

export function GameDetailModal({ game, groupSize, onClose }: GameDetailModalProps) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const exp = game.coopExperience
  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal large" onClick={(e) => e.stopPropagation()}>
        <div className="cg-detail-hero">
          <GameCover game={game} size="lg" />
          <button className="cg-icon-btn cg-modal-close-x" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="cg-detail-modal-body">
          <h2>{game.title}</h2>
          <p className="cg-detail-desc">{game.desc}</p>
          {game.coop && (
            <div className="cg-coop">
              <CoopPlayersBadge game={game} groupSize={groupSize} />
              <CoopModesBadges coop={game.coop} />
              <CoopKindBadge coop={game.coop} />
              <CoopAttribution />
            </div>
          )}
          <div className="cg-detail-meta">
            {game.released && (
              <div className="cg-meta-row">
                <span className="cg-meta-label">Released</span>
                <span>{game.released}{game.earlyAccess && ' · Early Access'}</span>
              </div>
            )}
            {game.developer && (
              <div className="cg-meta-row">
                <span className="cg-meta-label">Developer</span>
                <span>{game.developer}</span>
              </div>
            )}
            {game.multiplayer && (
              <div className="cg-meta-row">
                <span className="cg-meta-label">Multiplayer</span>
                <span>{game.multiplayer}</span>
              </div>
            )}
            <HltbTimes game={game} />
          </div>
          <div className="cg-detail-meta-row">
            {game.rating > 0 && <RatingPill rating={game.rating} reviews={game.reviews} />}
            {game.currency && <PriceTag game={game} />}
          </div>

          {exp && (
            <div className="cg-coop-exp">
              <div className="cg-coop-exp-header">
                <div className="cg-coop-exp-mark">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm6 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-6 1c-2.2 0-4 1.3-4 3v2h8v-2c0-1.7-1.8-3-4-3zm6 0a4.5 4.5 0 0 0-1.5.25A3.7 3.7 0 0 1 11 11v2h4v-2c0-1.6-1.6-3-4-3z" />
                  </svg>
                </div>
                <div className="cg-coop-exp-title-block">
                  <div className="cg-coop-exp-title">The Co-Op Experience</div>
                  <div className="cg-coop-exp-source">
                    {exp.coopUrl
                      ? <a href={exp.coopUrl} target="_blank" rel="noopener noreferrer">From Co-Optimus</a>
                      : 'From Co-Optimus'}
                  </div>
                </div>
              </div>
              <p className="cg-coop-exp-summary">{exp.summary}</p>
              {exp.maxOnline != null && (
                <div className="cg-coop-exp-facts">
                  <div className="cg-coop-exp-fact">
                    <span className="cg-coop-exp-fact-label">Recommended players</span>
                    <span className="cg-coop-exp-fact-value">{exp.maxOnline}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
